import { Badge } from '@/components/ui/badge';
import { EntityLink } from '@/components/ui/entity-link';
import { useEntityModalContext } from '@/contexts/EntityModalContext';
import { cn } from '@/lib/utils';
import { Building2, User } from 'lucide-react';

interface CustomerDisplayItemProps {
	customerId?: string | null;
	displayName?: string | null;
	email?: string | null;
	isCompany?: boolean | null;
	customerTypeName?: string | null;
	companyLabel?: string;
	individualLabel?: string;
	isActive?: boolean | null;
	inactiveLabel?: string;
	tags?: string[];
	note?: string | null;
	singleLine?: boolean;
	className?: string;
}

const compact = (value?: string | null) => {
	const normalized = value?.trim();
	return normalized ? normalized : null;
};

export function CustomerDisplayItem({
	customerId,
	displayName,
	email,
	isCompany,
	customerTypeName,
	companyLabel = 'Company',
	individualLabel = 'Individual',
	isActive,
	inactiveLabel = 'Inactive',
	tags = [],
	note,
	singleLine = false,
	className
}: CustomerDisplayItemProps) {
	const { openEntityModal } = useEntityModalContext();
	const IconComponent = isCompany ? Building2 : User;
	const displayText =
		compact(displayName) ?? compact(customerId) ?? 'Unknown customer';

	const resolvedType =
		compact(customerTypeName) ??
		(typeof isCompany === 'boolean'
			? isCompany
				? companyLabel
				: individualLabel
			: null);

	const detailsText = [resolvedType, compact(email)]
		.filter((value): value is string => Boolean(value))
		.join(' - ');

	const shouldShowInactiveBadge =
		typeof isActive === 'boolean' && isActive === false;

	const uniqueTags = Array.from(
		new Set(
			tags
				.map(tag => tag.trim())
				.filter(tag => Boolean(tag))
		)
	);

	const noteValue = compact(note);

	if (singleLine) {
		return (
			<div className={cn('bg-transparent', className)}>
				<div className="flex items-center gap-3">
					<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-transparent dark:border-gray-600 overflow-hidden">
						<IconComponent className="h-4 w-4 text-gray-400 dark:text-gray-400" />
					</div>
					<div className="min-w-0 flex flex-1 items-center gap-3">
						<div className="min-w-0 flex items-center gap-3">
							{customerId ? (
								<EntityLink
									entityId={customerId}
									entityType="customers"
									displayText={displayText}
									onEntityClick={openEntityModal}
								className="truncate whitespace-nowrap text-sm font-medium text-primary"
							/>
							) : (
								<p className="truncate whitespace-nowrap text-sm font-medium text-foreground">
									{displayText}
								</p>
							)}
							{detailsText && (
								<span className="truncate whitespace-nowrap text-sm text-muted-foreground">
									{detailsText}
								</span>
							)}
							{noteValue && (
								<span className="truncate whitespace-nowrap text-sm text-muted-foreground">
									{noteValue}
								</span>
							)}
						</div>
						<div className="ml-auto flex flex-shrink-0 items-center gap-2">
							{uniqueTags.length > 0 &&
								uniqueTags.map(tag => (
									<Badge key={tag} variant="outline">
										{tag}
									</Badge>
								))}
							{shouldShowInactiveBadge && (
								<Badge
									variant="outline"
									className="border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200"
								>
									{inactiveLabel}
								</Badge>
							)}
						</div>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className={cn('bg-transparent', className)}>
			<div className="flex items-start gap-3">
				<div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-gray-200 bg-transparent dark:border-gray-600 overflow-hidden">
					<IconComponent className="h-4 w-4 text-gray-400 dark:text-gray-400" />
				</div>
				<div className="min-w-0 flex-1">
					<div className="flex flex-wrap items-center justify-between gap-2">
						<div className="min-w-0">
							{customerId ? (
								<EntityLink
									entityId={customerId}
									entityType="customers"
									displayText={displayText}
									onEntityClick={openEntityModal}
									className="text-sm font-medium text-primary"
								/>
							) : (
								<p className="truncate text-sm font-medium text-foreground">
									{displayText}
								</p>
							)}
						</div>
						{shouldShowInactiveBadge && (
							<Badge
								variant="outline"
								className="border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800/60 dark:bg-amber-950/30 dark:text-amber-200"
							>
								{inactiveLabel}
							</Badge>
						)}
					</div>
					{detailsText && (
						<p className="mt-1 text-sm text-muted-foreground">
							{detailsText}
						</p>
					)}
					{uniqueTags.length > 0 && (
						<div className="mt-2 flex flex-wrap gap-1.5">
							{uniqueTags.map(tag => (
								<Badge key={tag} variant="outline">
									{tag}
								</Badge>
							))}
						</div>
					)}
					{noteValue && (
						<p className="mt-1 text-sm text-muted-foreground">
							{noteValue}
						</p>
					)}
				</div>
			</div>
		</div>
	);
}
